import { 
  GenerateDesignRequest, 
  DesignResult, 
  DesignError
} from '@/types/api';
import { convertFileToBase64 } from './imageUtils';
import { API_CONFIG } from '@/utils/constants';

class GeminiService {
  private apiEndpoint = '/api/generate-design';

  async generateDesign(request: GenerateDesignRequest): Promise<DesignResult> {
    try {
      // Convert image to base64
      const imageData = await convertFileToBase64(request.image);
      
      // Prepare request body
      const requestBody = {
        image: imageData,
        preferences: request.preferences,
        language: request.language,
      };

      // Make API request with retry logic
      let lastError: Error;
      for (let attempt = 1; attempt <= API_CONFIG.MAX_RETRIES; attempt++) {
        try {
          console.log(`Attempting to generate design (attempt ${attempt}/${API_CONFIG.MAX_RETRIES})`);
          
          const response = await fetch(this.apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            let errorMessage = 'API request failed';
            try {
              const errorData = await response.json();
              errorMessage = errorData.error || errorMessage;
            } catch {
              errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            
            throw new DesignError(errorMessage, this.getErrorCode(response.status));
          }

          const result = await response.json();
          
          // Validate response
          if (!result.image || !result.description) {
            throw new DesignError('Incomplete response from server', 'GENERATION_ERROR');
          }

          console.log('Design generated successfully');
          return {
            image: result.image,
            description: result.description,
          };
          
        } catch (error) {
          lastError = error instanceof Error ? error : new Error('Unknown error');
          
          // Don't retry for certain errors
          if (error instanceof DesignError) {
            if (error.code === 'API_ERROR' && lastError.message.includes('400')) {
              throw error; // Bad request, don't retry
            }
          }
          
          if (attempt < API_CONFIG.MAX_RETRIES) {
            // Wait before retry (exponential backoff)
            const delay = Math.pow(2, attempt) * 1000;
            console.log(`Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }
      }
      
      throw lastError!;
      
    } catch (error) {
      console.error('Design generation failed:', error);
      
      if (error instanceof DesignError) {
        throw error;
      }
      
      // Handle different error types
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new DesignError('Network error. Please check your connection.', 'NETWORK_ERROR');
      }
      
      throw new DesignError(
        `Failed to generate design: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'GENERATION_ERROR'
      );
    }
  }

  private getErrorCode(status: number): DesignError['code'] {
    if (status >= 500) return 'API_ERROR';
    if (status === 429) return 'API_ERROR'; // Rate limit
    if (status >= 400) return 'GENERATION_ERROR';
    return 'NETWORK_ERROR';
  }
}

// Export singleton instance
export const geminiService = new GeminiService();